'use client';

import {useEffect, useRef, useState} from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {usePathname} from 'next/navigation';
import {FiChevronDown, FiMenu, FiX} from 'react-icons/fi';
import logo from '@/public/static/assets/logo.png';
import {buttonClass} from '@/components/Button';
import {MAIN_NAV, PRODUCT_CATEGORIES_NAV} from '@/lib/navigation';
import {track, trackArea, trackAttrs} from '@/lib/analytics';

const isActive = (pathname: string, href: string) => pathname === href || pathname.startsWith(href);

const navLink = (active: boolean) =>
  `rounded-full px-4 py-2 text-[0.95rem] font-medium no-underline transition-colors ${
    active ? 'bg-brand-50 text-brand-800' : 'text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900'
  }`;

/** Sticky site header: desktop navigation with product dropdown, slide-down menu on mobile. */
const SiteHeader = () => {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const productsRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const productsActive = pathname.startsWith('/produkt');

  // Escape closes the dropdown / mobile menu (focus returns to the toggle); outside click closes the dropdown.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      setProductsOpen((wasOpen) => {
        if (wasOpen) {
          productsRef.current?.querySelector('button')?.focus();
        }
        return false;
      });
      setOpen((wasOpen) => {
        if (wasOpen) {
          menuButtonRef.current?.focus();
        }
        return false;
      });
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!productsRef.current?.contains(event.target as Node)) {
        setProductsOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, []);

  return (
    <header className='sticky top-0 z-40 border-b border-zinc-200/80 bg-white/85 backdrop-blur-md' {...trackArea('header')}>
      <div className='container-site flex h-[var(--header-height)] items-center justify-between gap-6'>
        <Link href='/' className='shrink-0' onClick={() => setOpen(false)} aria-label='Pernikl – Startseite'>
          <Image src={logo} alt='Pernikl Logo' height={40} priority className='h-10 w-auto' />
        </Link>

        <nav className='hidden items-center gap-1 lg:flex' aria-label='Hauptnavigation'>
          <div
            ref={productsRef}
            className='relative'
            onMouseEnter={() => setProductsOpen(true)}
            onMouseLeave={() => setProductsOpen(false)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                setProductsOpen(false);
              }
            }}>
            <button
              type='button'
              className={`${navLink(productsActive)} inline-flex cursor-pointer items-center gap-1`}
              aria-expanded={productsOpen}
              aria-controls='produkte-menu'
              onClick={() => setProductsOpen(!productsOpen)}>
              Produkte
              <FiChevronDown className={`transition-transform ${productsOpen ? 'rotate-180' : ''}`} aria-hidden />
            </button>
            <div
              id='produkte-menu'
              hidden={!productsOpen}
              className='absolute top-full left-0 pt-3'>
              <ul className='w-80 rounded-2xl bg-white p-2 shadow-xl ring-1 ring-zinc-200'>
                {PRODUCT_CATEGORIES_NAV.map((item) => (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      onClick={() => setProductsOpen(false)}
                      aria-current={isActive(pathname, item.href) ? 'page' : undefined}
                      className='block rounded-xl px-4 py-3 no-underline transition-colors hover:bg-zinc-50'>
                      <span className='block font-medium text-zinc-900'>{item.label}</span>
                      <span className='block text-sm text-zinc-500'>{item.description}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {MAIN_NAV.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={navLink(isActive(pathname, item.href))}
              aria-current={isActive(pathname, item.href) ? 'page' : undefined}>
              {item.label}
            </Link>
          ))}
        </nav>

        <div className='flex items-center gap-2'>
          <Link
            href='/kontakt/'
            className={`${buttonClass('primary')} hidden sm:inline-flex`}
            {...trackAttrs('cta_click', {cta: 'kontakt', location: 'header'})}>
            Kontakt
          </Link>
          <button
            ref={menuButtonRef}
            type='button'
            className='inline-flex size-11 items-center justify-center rounded-full text-zinc-800 hover:bg-zinc-100 lg:hidden'
            aria-label={open ? 'Menü schließen' : 'Menü öffnen'}
            aria-expanded={open}
            aria-controls='mobile-menu'
            onClick={() => {
              if (!open) {
                track('menu_open', {location: 'mobile'});
              }
              setOpen(!open);
            }}>
            {open ? <FiX className='size-6' /> : <FiMenu className='size-6' />}
          </button>
        </div>
      </div>

      {open && (
        <nav
          id='mobile-menu'
          className='border-t border-zinc-200 bg-white lg:hidden'
          aria-label='Mobile Navigation'
          {...trackArea('mobile_menu')}>
          <div className='container-site flex max-h-[calc(100svh-var(--header-height))] flex-col gap-1 overflow-y-auto py-4'>
            <p className='px-4 pt-2 pb-1 text-xs font-semibold tracking-wider text-zinc-500 uppercase'>Produkte</p>
            {[...PRODUCT_CATEGORIES_NAV, ...MAIN_NAV].map((item, index) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                aria-current={isActive(pathname, item.href) ? 'page' : undefined}
                className={`${navLink(isActive(pathname, item.href))} ${index === PRODUCT_CATEGORIES_NAV.length ? 'mt-3' : ''} py-3 text-base`}>
                {item.label}
              </Link>
            ))}
            <Link
              href='/kontakt/'
              onClick={() => setOpen(false)}
              className={`${buttonClass('primary')} mt-4`}
              {...trackAttrs('cta_click', {cta: 'kontakt', location: 'mobile_menu'})}>
              Kontakt aufnehmen
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
};

export default SiteHeader;
